import type { NoraWebViewEvent } from '#ui-shared/stores/webview/webview.types';

/**
 * Bridge used when the webview runs *inside* the browser-based Native Layer
 * Simulator (an iframe hosted by the nora-ui `/simulator` route).
 *
 * On a real device the webview talks to the native layer over a serialized
 * `postMessage` bridge (Android even requires a JSON string). This module
 * reproduces that exact boundary: the webview posts its events to the parent
 * window (the simulator), and receives native -> webview events back as
 * `postMessage`s, which are re-dispatched as the same `window` CustomEvents the
 * webview store already listens for. Because it round-trips through JSON, it
 * surfaces serialization bugs that an in-process simulator would hide.
 */

/** Query param the simulator appends to the iframe URL to mark it as the simulated webview. */
export const WEBVIEW_SIMULATOR_CHILD_QUERY_PARAM = 'simulatorChild';

/** Marks a message posted by the webview to the simulator parent. */
const WEBVIEW_MESSAGE_MARKER = '__noraWebviewEvent';
/** Marks a message posted by the simulator parent to the webview. */
const NATIVE_MESSAGE_MARKER = '__noraNativeEvent';
/** Marks a runtime status snapshot the webview posts to the simulator parent. */
const WEBVIEW_STATUS_MARKER = '__noraWebviewStatus';

export interface SimulatorWebviewMessage {
  [WEBVIEW_MESSAGE_MARKER]: true;
  event: NoraWebViewEvent;
}

export interface SimulatorNativeMessage {
  [NATIVE_MESSAGE_MARKER]: true;
  pattern: string;
  data: unknown;
}

/**
 * A snapshot of the runtime state the webview tracks internally, forwarded to the
 * simulator parent so the control panel can show connectivity, the SSE stream and
 * the bootstrap phase. Kept as plain strings so the bridge stays decoupled from the
 * app-specific connectivity/bootstrap enums.
 */
export interface SimulatorWebviewStatus {
  /** Device network as the webview sees it (browser signal or native CONNECTIVITY_CHANGED). */
  networkOnline: boolean;
  /** Whether the SSE stream to the backend is currently open. */
  streamConnected: boolean;
  /** Derived connectivity status (e.g. connected / connecting / disconnected). */
  connectivityStatus: string;
  /** Current bootstrap phase (e.g. idle / initializing / loading / ready / no-user / error). */
  bootstrapPhase: string;
}

interface SimulatorWebviewStatusMessage {
  [WEBVIEW_STATUS_MARKER]: true;
  status: SimulatorWebviewStatus;
}

/**
 * Whether this document is the simulated webview running inside the simulator iframe.
 *
 * Requires both an actual parent frame and the explicit query-param marker, so a
 * normal (device) webview is never affected even if it were embedded.
 */
export function isWebViewSimulatorChild(): boolean {
  if (typeof window === 'undefined' || window.parent === window.self) {
    return false;
  }

  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(WEBVIEW_SIMULATOR_CHILD_QUERY_PARAM) === 'true';
}

/** Posts a webview -> native event to the simulator parent, serialized like the device bridge. */
export function postEventToSimulatorParent(event: NoraWebViewEvent): void {
  const message: SimulatorWebviewMessage = {
    [WEBVIEW_MESSAGE_MARKER]: true,
    event,
  };

  window.parent.postMessage(JSON.stringify(message), window.location.origin);
}

/** Posts the webview's current runtime status snapshot to the simulator parent. */
export function postStatusToSimulatorParent(status: SimulatorWebviewStatus): void {
  const message: SimulatorWebviewStatusMessage = {
    [WEBVIEW_STATUS_MARKER]: true,
    status,
  };

  window.parent.postMessage(JSON.stringify(message), window.location.origin);
}

let bridgeListenerRegistered = false;

/**
 * Listens for native -> webview events from the simulator parent and forwards
 * each to `dispatchNativeEvent`, which the store wires to its existing
 * `window` CustomEvent dispatch.
 * @param dispatchNativeEvent - invoked with the event pattern and payload
 */
export function setupSimulatorParentBridge(
  dispatchNativeEvent: (pattern: string, data: unknown) => void,
): void {
  if (bridgeListenerRegistered) {
    return;
  }

  bridgeListenerRegistered = true;

  window.addEventListener('message', (messageEvent: MessageEvent) => {
    if (messageEvent.origin !== window.location.origin) {
      return;
    }

    if (typeof messageEvent.data !== 'string') {
      return;
    }

    let parsedMessage: SimulatorNativeMessage;

    try {
      parsedMessage = JSON.parse(messageEvent.data) as SimulatorNativeMessage;
    }
    catch {
      return;
    }

    if (!parsedMessage || parsedMessage[NATIVE_MESSAGE_MARKER] !== true) {
      return;
    }

    dispatchNativeEvent(parsedMessage.pattern, parsedMessage.data);
  });
}

/* ------------------------------------------------------------------ *
 * Parent (simulator / Native Layer) side of the protocol.
 * ------------------------------------------------------------------ */

/**
 * Parses a raw `postMessage` payload received by the simulator from the webview.
 * @param raw - the `MessageEvent.data`
 * @returns the webview event, or `null` if the message is not ours
 */
export function parseSimulatorWebviewMessage(raw: unknown): NoraWebViewEvent | null {
  if (typeof raw !== 'string') {
    return null;
  }

  let parsedMessage: SimulatorWebviewMessage;

  try {
    parsedMessage = JSON.parse(raw) as SimulatorWebviewMessage;
  }
  catch {
    return null;
  }

  if (!parsedMessage || parsedMessage[WEBVIEW_MESSAGE_MARKER] !== true) {
    return null;
  }

  return parsedMessage.event;
}

/**
 * Parses a runtime status snapshot posted by the webview.
 * @param raw - the `MessageEvent.data`
 * @returns the status snapshot, or `null` if the message is not a status message
 */
export function parseSimulatorStatusMessage(raw: unknown): SimulatorWebviewStatus | null {
  if (typeof raw !== 'string') {
    return null;
  }

  let parsedMessage: SimulatorWebviewStatusMessage;

  try {
    parsedMessage = JSON.parse(raw) as SimulatorWebviewStatusMessage;
  }
  catch {
    return null;
  }

  if (!parsedMessage || parsedMessage[WEBVIEW_STATUS_MARKER] !== true) {
    return null;
  }

  return parsedMessage.status;
}

/** Builds a native -> webview message for the simulator to post into the iframe. */
export function buildSimulatorNativeMessage(pattern: string, data: unknown): string {
  const message: SimulatorNativeMessage = {
    [NATIVE_MESSAGE_MARKER]: true,
    pattern,
    data,
  };

  return JSON.stringify(message);
}
